import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Link2,
  RefreshCw,
  ArrowRight,
  TrendingUp,
  UserCheck,
  Globe,
  Linkedin,
  MessageCircle,
  Mail,
  Smartphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import GrowthDateFilter from '../Growth/GrowthDateFilter';
import { getSupportChannels } from '../../Services/SupportService';
import {
  SUPPORT_CHANNELS,
  SUPPORT_CHANNEL_LABELS,
  SupportChannel,
  SupportChannelKey,
} from '../../Types/SupportType';
import { channelPath, LINKS_TAB_PATH } from './SupportTabNav';

const channelIcon: Record<SupportChannelKey, React.ElementType> = {
  linkedin: Linkedin,
  whatsapp: MessageCircle,
  email: Mail,
  telegram: Smartphone,
  direct: Globe,
};

function BusinessSupportOverview() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState<SupportChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSupportChannels({
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
      });
      if (response.success && Array.isArray(response.data)) {
        setChannels(response.data);
      }
    } catch (err) {
      console.error('Failed to load support channels', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = useMemo(() => {
    const acc = channels.reduce(
      (total, c) => ({
        visitors: total.visitors + c.visitors,
        registrationStarted: total.registrationStarted + c.registrationStarted,
        registered: total.registered + c.registered,
        approved: total.approved + c.approved,
        notRegistered: total.notRegistered + c.notRegistered,
      }),
      { visitors: 0, registrationStarted: 0, registered: 0, approved: 0, notRegistered: 0 }
    );
    const conversionPct = acc.visitors ? (acc.registered / acc.visitors) * 100 : 0;
    return { ...acc, conversionPct };
  }, [channels]);

  const stats = useMemo(
    () => [
      { label: 'Visitors', value: summary.visitors, tone: 'text-slate-100' },
      {
        label: 'Not registered',
        value: summary.notRegistered,
        tone: 'text-amber-400',
      },
      { label: 'Started', value: summary.registrationStarted, tone: 'text-slate-300' },
      { label: 'Registered', value: summary.registered, tone: 'text-emerald-400' },
      { label: 'Approved', value: summary.approved, tone: 'text-violet-400' },
      {
        label: 'Conversion',
        value: `${summary.conversionPct.toFixed(1)}%`,
        tone: 'text-cyan-400',
      },
    ],
    [summary]
  );

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Users size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
            Business Support
          </h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Channel acquisition and visitor follow-up
          </p>
        </div>
        <button
          onClick={() => navigate(LINKS_TAB_PATH)}
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <Link2 size={16} />
          Generate Link
        </button>
        <button
          onClick={fetchData}
          title="Refresh"
          className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
        />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {stat.label}
            </div>
            <div className={`mt-1 text-xl font-black tabular-nums ${stat.tone}`}>
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </div>
          </div>
        ))}
      </div>

      {loading && channels.length === 0 ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {SUPPORT_CHANNELS.map((channel, idx) => {
            const Icon = channelIcon[channel];
            const data = channels.find((c) => c.channel === channel);
            const label = SUPPORT_CHANNEL_LABELS[channel];
            return (
              <motion.button
                key={channel}
                type="button"
                onClick={() => navigate(channelPath(channel))}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.35, delay: 0.1 + idx * 0.05 }}
                className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-left transition hover:border-cyan-500/40 hover:bg-slate-900"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                    <Icon size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-slate-100">{label}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      <Globe size={11} />
                      {data?.visitors ?? 0} visitors
                    </p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      <UserCheck size={11} className="text-emerald-400" />
                      {data?.registered ?? 0} registered
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-cyan-400" />
              </motion.button>
            );
          })}
        </div>
      )}

      {channels.length > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs font-semibold text-slate-500">
          <TrendingUp size={14} className="text-cyan-400" />
          Open a channel box to follow up with visitors who arrived and never finished registering.
        </div>
      )}
    </div>
  );
}

export default BusinessSupportOverview;